/**
 * Shared utility functions for spreadsheet tools
 * These utilities are platform-agnostic and can be used by both Excel and Sheets tools
 */

/**
 * Convert column number (1-based) to column letter (A, B, ..., Z, AA, AB, ...)
 * Used for formula adjustment and address manipulation
 */
export function columnNumberToLetter(column: number): string {
  let result = ''
  while (column > 0) {
    column--
    result = String.fromCharCode(65 + (column % 26)) + result
    column = Math.floor(column / 26)
  }
  return result
}

/**
 * Convert column letter to column number (1-based)
 * Used for formula adjustment and address manipulation
 */
export function columnLetterToNumber(letters: string): number {
  let colNum = 0
  const normalized = letters.toUpperCase()
  for (let i = 0; i < normalized.length; i++) {
    const code = normalized.charCodeAt(i)
    // A..Z => 1..26
    if (code < 65 || code > 90) return Number.NaN
    colNum = colNum * 26 + (code - 64)
  }
  return colNum
}

/**
 * Parse cell address into column letters and row number
 * Used for address manipulation and parsing
 */
export function parseCellAddress(address: string): {
  col: string
  row: number
} {
  const match = address.match(/^([A-Z]+)(\d+)$/i)
  if (!match) {
    throw new Error(`Invalid cell address: ${address}`)
  }
  return { col: match[1].toUpperCase(), row: parseInt(match[2], 10) }
}

/**
 * Adjusts cell references in a formula by row and column offsets
 * e.g., "=B2*C2" with rowOffset=1 becomes "=B3*C3"
 * Handles absolute references ($A$1, $A1, A$1) correctly
 *
 * This function is used when applying a formula to a range of cells,
 * where each cell needs the formula adjusted for its position.
 */
export function adjustFormulaReferences(
  formula: string,
  rowOffset: number,
  colOffset: number
): string {
  // Match cell references like A1, $A1, A$1, $A$1, AA123, etc.
  return formula.replace(
    /(\$?)([A-Z]+)(\$?)(\d+)/gi,
    (_match, colAbsolute, col, rowAbsolute, row) => {
      let newCol = col
      let newRow = row

      // Adjust column if not absolute
      if (colAbsolute !== '$' && colOffset !== 0) {
        // Convert column letters to number, add offset, convert back
        const colNum = columnLetterToNumber(col)
        if (!Number.isNaN(colNum)) {
          const newColNum = colNum + colOffset
          if (newColNum > 0) {
            newCol = columnNumberToLetter(newColNum)
          }
        }
      }

      // Adjust row if not absolute
      if (rowAbsolute !== '$' && rowOffset !== 0) {
        const rowNum = Number.parseInt(row, 10)
        if (!Number.isNaN(rowNum)) {
          const newRowNum = rowNum + rowOffset
          if (newRowNum > 0) {
            newRow = String(newRowNum)
          }
        }
      }

      return `${colAbsolute}${newCol}${rowAbsolute}${newRow}`
    }
  )
}
